import { Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { ConfirmPopup, ConfirmPopupService } from '@satin/core/components/confirmation-popup';
import { SharedConstant } from '@satin/core/constant';
import { ServiceLocator } from '@satin/core/injectors';
import { isNotEmpty, isNotNull, isNotEmptyArray } from '@satin/core/utility';

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Commonly used methods which can be used by any component which extends this class.
 * Note: this is not a regular Angular component (the '@Component' annotation is omitted)
 * and therefore it is not declared in any Angular module.
 *
 * @packageDocumentation
 */
class BasePageComponent {
    constructor() {
        this.pageForms = new Array();
        this.cancelFormPopup = new ConfirmPopup(SharedConstant.UNSAVED_CHANGES_DIALOG_TITLE, SharedConstant.UNSAVED_CHANGES_MESSAGE, true);
        this.confirmPopupService = ServiceLocator.injector.get(ConfirmPopupService);
    }
    /**
     * Utility method to add the validator.
     *
     * @param formControl The FormControl to which the validator should be added.
     * @param validatorList The list of validators to be applied to the formControl.
     */
    addValidator(formControl, validatorList) {
        if (isNotEmpty(validatorList)) {
            formControl.setValidators(validatorList);
        }
        else {
            formControl.setValidators([Validators.required]);
        }
        formControl.updateValueAndValidity();
    }
    /**
     * Calls the ConfirmPopupService to open the confirmation popup if the form is 'dirty' (i.e. has unsaved changes).
     *
     * @returns True if the form is not 'dirty' and deactivation is allowed, otherwise false.
     */
    canDeactivate() {
        let dirtyFlag = false;
        this.pageForms.forEach((form) => {
            if (isNotNull(form)) {
                if (form.dirty) {
                    dirtyFlag = true;
                }
            }
        });
        if (isNotNull(this.getForm())) {
            dirtyFlag = this.pageForm.dirty;
        }
        else if (isNotEmptyArray(this.pageForms)) {
            this.pageForms.forEach((form) => {
                if (isNotNull(form)) {
                    if (form.dirty) {
                        dirtyFlag = true;
                    }
                }
            });
        }
        if (dirtyFlag) {
            // Open popup.
            this.confirmPopupService.openPopup(this.cancelFormPopup);
            return this.confirmPopupService.getConfirmation().pipe(take(1));
        }
        else {
            return true;
        }
    }
    /**
     * Removes all forms from the pageForms FormGroup array.
     */
    removeForms() {
        this.pageForms = new Array();
        this.pageForm = null;
    }
    /**
     * Utility method to remove the validator so we don't have to repeat these lines.
     *
     * @param formControl The FormControl from which the validators should be removed.
     * @param resetFlag Boolean for whether or not the formControl should be reset.
     */
    removeValidators(formControl, resetFlag = true) {
        formControl.clearValidators();
        if (resetFlag) {
            formControl.reset();
        }
        formControl.updateValueAndValidity();
    }
    /**
     * Adds the current form to the pageForms array.
     *
     * @param form The FormGroup to be added to the pageForms array.
     */
    addFormToArray(form) {
        this.pageForms.push(form);
    }
    /**
     * Sets the singular element of FormGroup.
     *
     * @param form The FormGroup to be set as PageForm
     */
    setForm(form) {
        this.pageForm = form;
    }
    /**
     * Returns the singular instance of formGroup the current form to the pageForms array.
     */
    getForm() {
        return this.pageForm;
    }
    /**
     * Returns if the form is valid or not
     */
    isFormValid() {
        // If the form doesn't exist then we're going to just return true
        if (isNotNull(this.getForm())) {
            this.getForm().valid;
        }
        else {
            return true;
        }
    }
    /**
     * Returns the Raw Value of the singular instance of formGroup the current form to the pageForms array.
     */
    getFormValue() {
        return this.pageForm.getRawValue();
    }
    /**
     * Sets the Angular form property for each form in the pageForms array to 'pristine'.
     */
    setFormToPristine() {
        if (isNotNull(this.getForm())) {
            this.pageForm.markAsPristine();
        }
        if (isNotEmptyArray(this.pageForms)) {
            this.pageForms.forEach((form) => {
                if (isNotNull(form)) {
                    form.markAsPristine();
                }
            });
        }
    }
}

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BasePageComponent };
